// File: src/utils/dateUtils.ts
import { format, parseISO, addDays, subDays, isValid, startOfDay, addMinutes, getDay, isSameDay } from 'date-fns';
import { RecurrencePattern, AvailableTimeBlock, CoachAvailabilitySlot } from '../types';

export const formatDateToYYYYMMDD = (date: Date | string | number): string => {
  if (!date || !isValid(new Date(date))) return format(new Date(), 'yyyy-MM-dd');
  return format(new Date(date), 'yyyy-MM-dd');
};

// MODIFIED: Explicitly handle null, undefined, or empty string inputs.
export const formatDateForDisplay = (date: Date | string | number | null | undefined): string => {
  if (date === null || date === undefined || (typeof date === 'string' && date.trim() === '')) {
    return 'N/A'; // Return "N/A" for null, undefined, or empty/whitespace-only strings
  }
  
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  if (!dateObj || !isValid(dateObj)) {
    return 'N/A'; // Return "N/A" if parsing still results in an invalid date
  }
  return format(dateObj, 'MMMM d, yyyy');
};

export const parseDateFromYYYYMMDD = (dateString: string): Date => {
  return parseISO(dateString);
};

export const getTodayYYYYMMDD = (): string => {
  return formatDateToYYYYMMDD(new Date());
};

export const getNextDay = (date: Date | string): Date => {
  return addDays(startOfDay(new Date(date)), 1);
};

export const getPreviousDay = (date: Date | string): Date => {
  return subDays(startOfDay(new Date(date)), 1);
};

export const generateTimeSlots = (intervalMinutes: number = 30): string[] => {
  const slots: string[] = [];
  const totalSlots = (24 * 60) / intervalMinutes;
  let date = startOfDay(new Date(2000, 0, 1));

  for (let i = 0; i < totalSlots; i++) {
    slots.push(format(date, 'HH:mm:ss'));
    date = addMinutes(date, intervalMinutes);
  }
  return slots;
};

export const formatTimeToAMPM = (timeString: string): string => {
  if (!timeString || !/^\d{2}:\d{2}(:\d{2})?$/.test(timeString)) return '';
  try {
    const [hours, minutes] = timeString.split(':');
    const date = new Date(2000, 0, 1, parseInt(hours, 10), parseInt(minutes, 10));
    if (!isValid(date)) return '';
    return format(date, 'h:mm a');
  } catch (e) {
    console.error("Error formatting time to AM/PM:", e);
    return '';
  }
};

export const combineDateTime = (dateStr: string, timeStr: string): Date => {
    if (!dateStr || !timeStr || !isValid(parseISO(dateStr))) {
        return new Date(NaN);
    }
    try {
        return parseISO(`${dateStr}T${timeStr}`);
    } catch (e) {
        console.error("Error combining date and time:", e);
        return new Date(NaN);
    }
};

export const timeToMinutes = (timeStr: string): number => {
  if (!timeStr || !/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) return 0;
  const parts = timeStr.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parts.length > 2 ? parseInt(parts[2], 10) : 0;
  return hours * 60 + minutes + (seconds / 60); 
};

export const calculateDurationInSlots = (startTime: string, endTime: string, intervalMinutes: number): number => {
  if (startTime >= endTime) return 1; 
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const duration = endMinutes - startMinutes;
  return Math.max(1, Math.ceil(duration / intervalMinutes)); 
};

export const snapTimeTo15Minutes = (timeStr: string): string => {
  if (!timeStr) return '00:00:00';
  
  const parts = timeStr.split(':');
  let hours = parseInt(parts[0], 10);
  let minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) {
    if (timeStr.match(/^\d{2}:\d{2}$/)) {
        hours = parseInt(timeStr.substring(0,2), 10);
        minutes = parseInt(timeStr.substring(3,5), 10);
        if (isNaN(hours) || isNaN(minutes)) return '00:00:00';
    } else {
        return '00:00:00';
    }
  }

  let snappedMinutes = Math.round(minutes / 15) * 15;

  if (snappedMinutes >= 60) {
    hours = (hours + Math.floor(snappedMinutes / 60)) % 24;
    minutes = snappedMinutes % 60;
  } else {
    minutes = snappedMinutes;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
};

export const formatRecurrencePattern = (pattern: RecurrencePattern | null | undefined): string => {
  if (!pattern) return 'One-off';

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  let parts: string[] = [];

  if (pattern.frequency) {
    parts.push(pattern.frequency.charAt(0).toUpperCase() + pattern.frequency.slice(1));
  }

  if (pattern.days_of_week && pattern.days_of_week.length > 0) {
    const sortedDays = [...pattern.days_of_week].sort((a,b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b));
    const dayStr = sortedDays.map(d => dayNames[d]).join(', ');
    parts.push(`on ${dayStr}`);
  }

  if (pattern.until_date) {
    try {
        parts.push(`until ${formatDateForDisplay(pattern.until_date)}`);
    } catch (e) {}
  }

  return parts.join(' ');
};

export const generateAvailableTimeBlocks = (date: Date, availabilityRules: CoachAvailabilitySlot[]): AvailableTimeBlock[] => {
    const dayOfWeek = getDay(date);
    const generatedBlocks: AvailableTimeBlock[] = [];

    availabilityRules.forEach(rule => {
        if (!rule.is_active) return;
        const ruleStartDate = startOfDay(parseISO(rule.start_datetime));
        const ruleUntilDate = rule.recurrence_pattern?.until_date ? startOfDay(parseISO(rule.recurrence_pattern.until_date)) : null;

        let isValidDayForRule = false;
        if (rule.is_recurring) {
            if (rule.recurrence_pattern?.days_of_week?.includes(dayOfWeek) && date >= ruleStartDate && (!ruleUntilDate || date <= ruleUntilDate)) {
                isValidDayForRule = true;
            }
        } else {
            if (isSameDay(date, ruleStartDate)) {
                isValidDayForRule = true;
            }
        }
        
        if (isValidDayForRule) {
            const ruleStartDateTime = parseISO(rule.start_datetime);
            const ruleEndDateTime = parseISO(rule.end_datetime);
            const slotDuration = rule.max_session_duration_minutes;

            let currentSlotStart = new Date(date);
            currentSlotStart.setHours(ruleStartDateTime.getHours(), ruleStartDateTime.getMinutes(), 0, 0);
            
            let ruleEndTimeOnDay = new Date(date);
            ruleEndTimeOnDay.setHours(ruleEndDateTime.getHours(), ruleEndDateTime.getMinutes(), 0, 0);

            while(addMinutes(currentSlotStart, slotDuration) <= ruleEndTimeOnDay) {
                const currentSlotEnd = addMinutes(currentSlotStart, slotDuration);
                generatedBlocks.push({
                    start_time: currentSlotStart.toISOString(),
                    end_time: currentSlotEnd.toISOString(),
                    original_availability_id: rule.id,
                    is_recurring: rule.is_recurring,
                });
                currentSlotStart = currentSlotEnd;
            }
        }
    });
    return generatedBlocks;
};